import { SearchOutlined } from '@ant-design/icons';
import {
  Button,
  Divider,
  Empty,
  Flex,
  Input,
  List,
  Modal,
  Radio,
  Skeleton,
  Space,
  Spin,
  Typography,
  message,
  theme,
} from 'antd';
import axios from 'axios';
import { useContext, useEffect, useState } from 'react';

import { formatCurrency } from '../../../../utils/format';
import { AddOrderReturnContext } from '../addOrderReturnContext';

const { Search } = Input;
const { Text } = Typography;

/** Анхны ачаалалтад харуулах хийсвэр мөрийн тоо */
const SKELETON_ROWS = 4;

/** Нэг захиалгын мөр — дарахад сонгогдоно, hover/сонгогдсон төлөв нь дэвсгэрээр */
const OrderRow = ({ order, checked, onSelect, token }) => {
  const [hovered, setHovered] = useState(false);

  const background = checked
    ? token.colorPrimaryBg
    : hovered
      ? token.colorFillTertiary
      : undefined;

  return (
    <List.Item
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '10px 12px',
        marginBottom: 4,
        borderRadius: 8,
        cursor: 'pointer',
        background,
        // Сонгогдсоныг дэвсгэрээс гадна нимгэн хүрээгээр тодотгоно
        boxShadow: checked ? `inset 0 0 0 1px ${token.colorPrimaryBorder}` : undefined,
        transition: 'background-color 0.12s ease, box-shadow 0.12s ease',
      }}
    >
      <Flex align="center" gap={12} style={{ width: '100%' }}>
        <Flex vertical gap={2} flex={1} style={{ minWidth: 0 }}>
          <Text strong ellipsis>
            {order?.code ?? '-'}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
            {order?.customerName ? `${order.customerName} · ` : ''}
            {order?.customerPhone ?? '-'}
          </Text>
        </Flex>

        <Flex vertical align="flex-end" gap={2} style={{ whiteSpace: 'nowrap' }}>
          <Text strong>{formatCurrency(parseFloat(order?.totalServiceAmount) || 0)}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Хүргэлтийн дүн
          </Text>
        </Flex>

        {/* Мөр дээр дарахтай давхцаж, сонголтыг буцааж болзошгүй тул зогсооно */}
        <span onClick={(e) => e.stopPropagation()}>
          <Radio checked={checked} onChange={onSelect} />
        </span>
      </Flex>
    </List.Item>
  );
};

const ModalSelectOrder = ({ visible = false, onCancel = () => undefined }) => {
  const { selectOrder } = useContext(AddOrderReturnContext);
  const { token } = theme.useToken();

  const [orderList, setOrderList] = useState();
  const [selectedId, setSelectedId] = useState();
  const [loading, setLoading] = useState(false);

  const fetchReturnOrderList = async (searchValue = '', params = { page: 1 }) => {
    setLoading(true);
    // "D"-ээр эхэлбэл захиалгын дугаар, эс бөгөөс утасны дугаараар хайна
    const reqBody =
      searchValue?.slice(0, 1) === 'D'
        ? { ...params, code: searchValue }
        : { ...params, phoneNumber: searchValue };

    try {
      const { data } = await axios.post('/api/v1/request/getDeliveries', reqBody);
      if (data?.statusCode === 200) setOrderList(data?.result);
    } catch (error) {
      console.error('returnOrderList: ', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReturnOrderList();
  }, []);

  const onOk = () => {
    const order = orderList?.docs?.find((item) => item?._id === selectedId);
    if (!order) {
      message.error('Та захиалгаа сонгоно уу!');
      return;
    }
    selectOrder(order);
    onCancel();
  };

  const docs = orderList?.docs ?? [];

  const renderBody = () => {
    if (loading && docs.length === 0) {
      return (
        <div style={{ padding: '16px 12px 0' }}>
          {Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <Skeleton key={index} active title={false} paragraph={{ rows: 2, width: ['50%', '30%'] }} />
          ))}
        </div>
      );
    }

    if (docs.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Захиалга олдсонгүй."
          style={{ padding: '48px 0' }}
        />
      );
    }

    return (
      <Spin spinning={loading}>
        <List
          // Мөр бүр өөрийн radius/дэвсгэртэй тул хуваах зураас илүүц
          split={false}
          dataSource={docs}
          renderItem={(order) => (
            <OrderRow
              key={order?._id}
              order={order}
              checked={selectedId === order?._id}
              onSelect={() => setSelectedId(order?._id)}
              token={token}
            />
          )}
        />
      </Spin>
    );
  };

  return (
    <Modal
      title="Буцаах захиалгаа сонгоно уу"
      open={visible}
      onOk={onOk}
      onCancel={onCancel}
      width={640}
      centered
      footer={
        <Flex align="center" justify="space-between" gap={16}>
          {/* Сонголт хийгээгүй үед зай нь хэвээр үлдэж товчнууд үсрэхгүй */}
          <Text type="secondary">{selectedId ? '1 захиалга сонгогдсон' : ''}</Text>
          <Space>
            <Button onClick={onCancel}>Буцах</Button>
            <Button type="primary" onClick={onOk} disabled={!selectedId}>
              Захиалга сонгох
            </Button>
          </Space>
        </Flex>
      }
    >
      <Flex vertical gap={12} style={{ paddingTop: 16 }}>
        <Search
          placeholder="Захиалгын дугаар, утасны дугаараар хайх"
          allowClear
          enterButton={<SearchOutlined />}
          loading={loading}
          onSearch={(value) => fetchReturnOrderList(value)}
          // Хайлтаа цэвэрлэхэд бүх захиалга буцаж ирнэ
          onChange={(e) => {
            if (e.target.value === '') fetchReturnOrderList('');
          }}
        />
        <Divider style={{ margin: 0 }} />
      </Flex>

      <div style={{ width: '100%', maxHeight: 440, overflow: 'auto' }}>{renderBody()}</div>
    </Modal>
  );
};

export default ModalSelectOrder;
